import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Star, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";

const TRIPS = ["Georgia", "Baku (Azerbaijan)", "Armenia", "USA"];
const EMPTY = { name: "", rating: 5, text: "", trip: "", active: true };
const inp = "w-full rounded-xl bg-cream/5 border border-cream/15 px-4 py-2.5 text-cream outline-none focus:border-terra-soft placeholder:text-cream/40";

export function ReviewsManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/reviews").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const reset = () => { setForm(EMPTY); setEditId(null); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, rating: Number(form.rating) };
      if (editId) await api.put(`/admin/reviews/${editId}`, body);
      else await api.post("/admin/reviews", body);
      toast.success(editId ? "Review updated" : "Review added");
      reset();
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete review from ${r.name}?`)) return;
    try {
      await api.delete(`/admin/reviews/${r.id}`);
      toast.success("Review deleted");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="admin-reviews-manager">
      <form onSubmit={submit} className="lg:col-span-5 rounded-3xl bg-cream/5 border border-cream/10 p-6 space-y-4 h-fit" data-testid="review-form">
        <p className="font-display text-2xl font-bold">{editId ? "Edit review" : "Add a Google review"}</p>
        <p className="text-sm text-cream/60">Copy real reviews from your Google Maps listing. The first 4 visible reviews show on the home page.</p>
        <input required minLength={2} placeholder="Reviewer name" value={form.name} onChange={set("name")} className={inp} data-testid="review-form-name" />
        <div className="grid grid-cols-2 gap-3">
          <select value={form.rating} onChange={set("rating")} className={inp} data-testid="review-form-rating">
            {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n} className="bg-ink">{n} stars</option>)}
          </select>
          <input list="review-trips" placeholder="Trip (e.g. Georgia)" value={form.trip} onChange={set("trip")} className={inp} data-testid="review-form-trip" />
          <datalist id="review-trips">{TRIPS.map((t) => <option key={t} value={t} />)}</datalist>
        </div>
        <textarea required minLength={5} maxLength={600} rows={5} placeholder="Review text" value={form.text} onChange={set("text")} className={inp} data-testid="review-form-text" />
        <label className="flex items-center gap-3"><Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} data-testid="review-form-active" /> Show on website</label>
        <div className="flex gap-3">
          <button className="rounded-full bg-terra px-6 py-2.5 font-semibold hover:bg-[#a5370a] transition-colors" data-testid="review-form-submit">{editId ? "Save" : "Add review"}</button>
          {editId && <button type="button" onClick={reset} className="rounded-full border border-cream/20 px-5 py-2.5" data-testid="review-form-cancel">Cancel</button>}
        </div>
      </form>
      <div className="lg:col-span-7 space-y-3">
        {items.length === 0 && <p className="text-cream/60" data-testid="reviews-empty">No reviews yet. The reviews section stays hidden on the website until you add one.</p>}
        {items.map((r) => (
          <div key={r.id} className="rounded-3xl bg-cream/5 border border-cream/10 p-5 flex gap-4 items-start" data-testid={`admin-review-${r.id}`}>
            <div className="flex-1 min-w-0">
              <p className="font-display text-lg font-bold flex items-center gap-2 flex-wrap">
                {r.name}
                <span className="flex">{Array.from({ length: r.rating }).map((_, k) => <Star key={k} className="h-3.5 w-3.5 text-gold fill-gold" />)}</span>
                {r.trip && <span className="eyebrow text-cream/60">· {r.trip}</span>}
                {!r.active && <span className="eyebrow text-terra-soft">Hidden</span>}
              </p>
              <p className="mt-2 text-sm text-cream/80 line-clamp-3">{r.text}</p>
            </div>
            <button onClick={() => { setEditId(r.id); setForm({ name: r.name, rating: r.rating, text: r.text, trip: r.trip, active: r.active }); }}
              data-testid={`admin-review-edit-${r.id}`} aria-label="Edit" className="grid place-items-center h-10 w-10 rounded-full border border-cream/20 hover:bg-cream hover:text-ink transition-colors"><Pencil className="h-4 w-4" /></button>
            <button onClick={() => remove(r)} data-testid={`admin-review-delete-${r.id}`} aria-label="Delete"
              className="grid place-items-center h-10 w-10 rounded-full border border-cream/20 hover:bg-terra hover:border-terra transition-colors"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
